import { useTaxonsGetImageTaxonMatch } from '@/api/TaxonomyApi/generated/taxons/taxons';
import { useEffect, useMemo, useState } from 'react';
import {
  normalizeMatchResponse,
  type SpeciesAiMatchResult,
} from '../SpeciesAiSearchScreen/SpeciesAiSearchScreen.utils';
import { Platform } from 'react-native';

export interface UseSpeciesAiMatchOptions {
  fileUri?: string;
  fileType?: string;
  fileName?: string;
}

const buildMatchFile = async ({
  fileUri,
  fileType,
  fileName,
}: UseSpeciesAiMatchOptions): Promise<Blob> => {
  if (!fileUri) {
    throw new Error('Missing file uri');
  }
  if (Platform.OS === 'web') {
    const response = await fetch(fileUri);

    if (!response.ok) {
      throw new Error(`Failed to read file ${fileName ?? fileUri}`);
    }

    const blob = await response.blob();
    const mimeType = fileType ?? blob.type;

    return new File([blob], fileName ?? '', {
      type: mimeType || 'application/octet-stream',
    });
  }

  return {
    uri: fileUri,
    name: fileName,
    type: fileType ?? 'application/octet-stream',
  } as unknown as Blob;
};

export const useSpeciesAiMatch = ({
  fileUri,
  fileType,
  fileName,
}: UseSpeciesAiMatchOptions): {
  data: SpeciesAiMatchResult | null;
  isLoading: boolean;
} => {
  const [data, setData] = useState<SpeciesAiMatchResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { mutateAsync: getTaxonMatch } = useTaxonsGetImageTaxonMatch();

  const triggerKey = useMemo(
    () => [fileUri, fileType, fileName].filter(Boolean).join('|'),
    [fileName, fileType, fileUri],
  );

  useEffect(() => {
    let isActive = true;

    if (!fileUri) {
      setData(null);
      setIsLoading(false);
      return (): void => {
        isActive = false;
      };
    }

    const requestMatch = async (): Promise<void> => {
      setIsLoading(true);
      setData(null);

      try {
        const file = await buildMatchFile({ fileUri, fileType, fileName });
        const match = await getTaxonMatch({ data: { file } });

        if (!isActive) {
          return;
        }

        setData(normalizeMatchResponse(match));
      } catch (error) {
        console.error('Failed to get taxon match', error);

        if (!isActive) {
          return;
        }

        setData({ bestMatch: null, otherMatches: [] });
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void requestMatch();

    return (): void => {
      isActive = false;
    };
  }, [fileUri, fileName, fileType, getTaxonMatch, triggerKey]);

  return { data, isLoading };
};
